// AllTradesHistory/StrategyBreakdown.jsx - With XLSX Export
import React, { useState, useMemo } from 'react';
import {
  Box,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  alpha,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Sort as SortIcon,
  OpenInNew as OpenInNewIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { COLORS, formatPnL } from '../TradeHistory/constants';

const DEFAULT_SLIPPAGE_COST_PCT = 0.05;

const formatPercent = (value, showSign = true) => {
  if (value === null || value === undefined || isNaN(value)) return '—';
  const num = Number(value);
  const sign = showSign ? (num >= 0 ? '+' : '') : '';
  return `${sign}${num.toFixed(2)}%`;
};

const StrategyBreakdown = ({ strategies, trades = [] }) => {
  const [sortBy, setSortBy] = useState('adjusted_total_pnl_pct');
  const [sortDir, setSortDir] = useState('desc');

  const enhancedStrategies = useMemo(() => {
    const tradesByStrategy = {};
    
    trades.forEach(trade => {
      const strategyId = trade.bulk_strategy_id;
      if (!tradesByStrategy[strategyId]) {
        tradesByStrategy[strategyId] = [];
      }
      tradesByStrategy[strategyId].push(trade);
    });

    return strategies.map(s => {
      const strategyTrades = tradesByStrategy[s.bulk_strategy_id] || [];
      const totalTrades = s.total_trades || strategyTrades.length || 0;
      
      const totalPnLPct = strategyTrades.reduce((sum, t) => sum + (parseFloat(t.pnl_pct) || 0), 0);
      const avgPnLPct = totalTrades > 0 ? totalPnLPct / totalTrades : 0;
      
      const adjustedTotalPnLPct = totalPnLPct - (totalTrades * DEFAULT_SLIPPAGE_COST_PCT);
      const adjustedAvgPnLPct = totalTrades > 0 ? adjustedTotalPnLPct / totalTrades : 0;
      
      const adjustedWinning = strategyTrades.filter(t => 
        ((parseFloat(t.pnl_pct) || 0) - DEFAULT_SLIPPAGE_COST_PCT) > 0
      ).length;
      const adjustedWinRate = totalTrades > 0 ? (adjustedWinning / totalTrades) * 100 : 0;
      
      return {
        ...s,
        total_pnl_pct: totalPnLPct,
        avg_pnl_pct: avgPnLPct,
        adjusted_total_pnl_pct: adjustedTotalPnLPct,
        adjusted_avg_pnl_pct: adjustedAvgPnLPct,
        adjusted_winning: adjustedWinning,
        adjusted_win_rate: adjustedWinRate,
      };
    });
  }, [strategies, trades]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
  };

  const sortedStrategies = useMemo(() => {
    return [...enhancedStrategies].sort((a, b) => {
      const aVal = a[sortBy] || 0;
      const bVal = b[sortBy] || 0;
      return sortDir === 'desc' ? bVal - aVal : aVal - bVal;
    });
  }, [enhancedStrategies, sortBy, sortDir]);

  // Export to XLSX
  const handleExportXLSX = () => {
    if (!sortedStrategies?.length) return;

    const exportData = sortedStrategies.map((s, idx) => ({
      '#': idx + 1,
      'Strategy': s.bulk_strategy_id || '',
      'Direction': s.direction || '',
      'Trades': s.total_trades || 0,
      'Win Rate %': parseFloat(s.win_rate) || 0,
      'Adj Win Rate %': parseFloat(s.adjusted_win_rate?.toFixed(1)) || 0,
      'P&L ($)': parseFloat(s.total_pnl) || 0,
      'P&L %': parseFloat(s.total_pnl_pct?.toFixed(2)) || 0,
      'Adj P&L %': parseFloat(s.adjusted_total_pnl_pct?.toFixed(2)) || 0,
      'Avg Adj %': parseFloat(s.adjusted_avg_pnl_pct?.toFixed(4)) || 0,
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    
    ws['!cols'] = [
      { wch: 5 },   // #
      { wch: 35 },  // Strategy
      { wch: 10 },  // Direction
      { wch: 8 },   // Trades
      { wch: 12 },  // Win Rate %
      { wch: 14 },  // Adj Win Rate %
      { wch: 12 },  // P&L ($)
      { wch: 10 },  // P&L %
      { wch: 12 },  // Adj P&L %
      { wch: 12 },  // Avg Adj %
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Strategy Performance');
    
    const filename = `strategy-performance-${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  const SortableHeader = ({ field, label, color = COLORS.text.muted }) => (
    <TableCell
      onClick={() => handleSort(field)}
      sx={{
        bgcolor: COLORS.bg.tertiary,
        color: sortBy === field ? COLORS.primary : color,
        fontWeight: 700,
        fontSize: '0.65rem',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        borderBottom: `1px solid ${alpha(COLORS.border, 0.5)}`,
        py: 1.5,
        cursor: 'pointer',
        '&:hover': { color: COLORS.primary },
        userSelect: 'none',
        whiteSpace: 'nowrap',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <span>{label}</span>
        {sortBy === field && (
          <SortIcon sx={{ fontSize: 12, transform: sortDir === 'asc' ? 'rotate(180deg)' : 'none' }} />
        )}
      </Stack>
    </TableCell>
  );

  return (
    <Box>
      {/* Export Button */}
      <Stack 
        direction="row" 
        justifyContent="flex-end" 
        alignItems="center" 
        sx={{ mb: 1.5 }}
      >
        <Tooltip title="Export to Excel (.xlsx)">
          <IconButton 
            onClick={handleExportXLSX}
            size="small"
            sx={{ 
              bgcolor: alpha(COLORS.success, 0.1), 
              '&:hover': { bgcolor: alpha(COLORS.success, 0.2) } 
            }}
          >
            <DownloadIcon sx={{ color: COLORS.success, fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Stack>

      <TableContainer sx={{ maxHeight: 500 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ bgcolor: COLORS.bg.tertiary, color: COLORS.text.muted, fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', borderBottom: `1px solid ${alpha(COLORS.border, 0.5)}`, py: 1.5 }}>
                Strategy
              </TableCell>
              <TableCell sx={{ bgcolor: COLORS.bg.tertiary, color: COLORS.text.muted, fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', borderBottom: `1px solid ${alpha(COLORS.border, 0.5)}`, py: 1.5 }}>
                Dir
              </TableCell>
              <SortableHeader field="total_trades" label="Trades" />
              <SortableHeader field="win_rate" label="Win %" />
              <SortableHeader field="adjusted_win_rate" label="Adj Win %" color={COLORS.warning} />
              <SortableHeader field="total_pnl" label="P&L $" />
              <SortableHeader field="total_pnl_pct" label="P&L %" />
              <SortableHeader field="adjusted_total_pnl_pct" label="Adj P&L %" color={COLORS.warning} />
              <SortableHeader field="adjusted_avg_pnl_pct" label="Avg Adj %" color={COLORS.warning} />
              <TableCell sx={{ bgcolor: COLORS.bg.tertiary, color: COLORS.text.muted, fontWeight: 700, fontSize: '0.65rem', textTransform: 'uppercase', borderBottom: `1px solid ${alpha(COLORS.border, 0.5)}`, py: 1.5, width: 40 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedStrategies.map((strategy) => {
              const isLong = strategy.direction === 'LONG';
              const isProfitable = strategy.total_pnl >= 0;
              const isPnLPctPositive = strategy.total_pnl_pct >= 0;
              const isAdjustedProfitable = strategy.adjusted_total_pnl_pct >= 0;

              return (
                <TableRow key={strategy.bulk_strategy_id} sx={{ '&:hover': { bgcolor: alpha(COLORS.primary, 0.03) } }}>
                  <TableCell sx={{ py: 1.25, borderBottom: `1px solid ${alpha(COLORS.border, 0.3)}` }}>
                    <Typography
                      variant="body2"
                      sx={{ color: COLORS.primary, fontWeight: 600, fontSize: '0.75rem', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={strategy.bulk_strategy_id}
                    >
                      {strategy.bulk_strategy_id}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ py: 1.25, borderBottom: `1px solid ${alpha(COLORS.border, 0.3)}` }}>
                    <Chip
                      icon={isLong ? <TrendingUpIcon sx={{ fontSize: 12 }} /> : <TrendingDownIcon sx={{ fontSize: 12 }} />}
                      label={strategy.direction}
                      size="small"
                      sx={{
                        bgcolor: alpha(isLong ? COLORS.success : COLORS.danger, 0.15),
                        color: isLong ? COLORS.success : COLORS.danger,
                        fontWeight: 600,
                        fontSize: '0.6rem',
                        height: 20,
                        '& .MuiChip-icon': { color: isLong ? COLORS.success : COLORS.danger },
                      }}
                    />
                  </TableCell>

                  <TableCell sx={{ py: 1.25, borderBottom: `1px solid ${alpha(COLORS.border, 0.3)}` }}>
                    <Typography variant="body2" sx={{ color: COLORS.text.secondary, fontWeight: 600, fontFamily: 'monospace' }}>
                      {strategy.total_trades}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ py: 1.25, borderBottom: `1px solid ${alpha(COLORS.border, 0.3)}` }}>
                    <Chip
                      label={`${strategy.win_rate}%`}
                      size="small"
                      sx={{
                        bgcolor: alpha(strategy.win_rate >= 50 ? COLORS.success : COLORS.danger, 0.15),
                        color: strategy.win_rate >= 50 ? COLORS.success : COLORS.danger,
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        height: 22,
                      }}
                    />
                  </TableCell>

                  <TableCell sx={{ py: 1.25, borderBottom: `1px solid ${alpha(COLORS.border, 0.3)}` }}>
                    <Chip
                      label={`${strategy.adjusted_win_rate.toFixed(1)}%`}
                      size="small"
                      sx={{
                        bgcolor: alpha(strategy.adjusted_win_rate >= 50 ? COLORS.success : COLORS.danger, 0.15),
                        color: strategy.adjusted_win_rate >= 50 ? COLORS.success : COLORS.danger,
                        fontWeight: 700,
                        fontSize: '0.65rem',
                        height: 20,
                      }}
                    />
                  </TableCell>

                  <TableCell sx={{ py: 1.25, borderBottom: `1px solid ${alpha(COLORS.border, 0.3)}` }}>
                    <Typography variant="body2" sx={{ color: isProfitable ? COLORS.success : COLORS.danger, fontWeight: 700, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {formatPnL(strategy.total_pnl)}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ py: 1.25, borderBottom: `1px solid ${alpha(COLORS.border, 0.3)}` }}>
                    <Typography variant="body2" sx={{ color: isPnLPctPositive ? COLORS.success : COLORS.danger, fontWeight: 600, fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {formatPercent(strategy.total_pnl_pct)}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ py: 1.25, borderBottom: `1px solid ${alpha(COLORS.border, 0.3)}` }}>
                    <Tooltip title={`Raw ${formatPercent(strategy.total_pnl_pct)} - ${strategy.total_trades} × ${DEFAULT_SLIPPAGE_COST_PCT}% slippage`}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: isAdjustedProfitable ? COLORS.success : COLORS.danger,
                          fontWeight: 700,
                          fontFamily: 'monospace',
                          fontSize: '0.8rem',
                          bgcolor: alpha(isAdjustedProfitable ? COLORS.success : COLORS.danger, 0.1),
                          px: 0.75,
                          py: 0.25,
                          borderRadius: 0.5,
                          display: 'inline-block',
                        }}
                      >
                        {formatPercent(strategy.adjusted_total_pnl_pct)}
                      </Typography>
                    </Tooltip>
                  </TableCell>

                  <TableCell sx={{ py: 1.25, borderBottom: `1px solid ${alpha(COLORS.border, 0.3)}` }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: strategy.adjusted_avg_pnl_pct >= 0 ? COLORS.success : COLORS.danger,
                        fontWeight: 600,
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                      }}
                    >
                      {formatPercent(strategy.adjusted_avg_pnl_pct)}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ py: 1.25, borderBottom: `1px solid ${alpha(COLORS.border, 0.3)}` }}>
                    <Tooltip title="View Strategy Details">
                      <IconButton size="small" href={`/officer/trade-history?strategy=${strategy.bulk_strategy_id}`} sx={{ color: COLORS.text.muted, '&:hover': { color: COLORS.primary } }}>
                        <OpenInNewIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default StrategyBreakdown;
